/* eslint-disable @typescript-eslint/no-explicit-any */

import './Address.css';

import { MenuItem, Select } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';

import { AddressData } from './AddressTypes';
import { boundingBoxCA, boundingBoxUS, IObjectKeys } from './GeoBoundingBox';

// Return the first address component whose "types" include any of the requested keys (e.g., 'locality', 'route').
const getByType = (comps: any[], ...types: string[]) =>
  comps.find((c) => types.some((t) => (c.types || []).includes(t)));

// Pick a city-like value from Google's components, trying common fields in order to handle regional differences.
const pickCity = (comps: any[]) => {
  return (
    getByType(comps, 'sublocality_level_1')?.shortText ??
    getByType(comps, 'locality')?.shortText ??
    getByType(comps, 'postal_town')?.shortText ??
    getByType(comps, 'sublocality')?.shortText ??
    getByType(comps, 'neighborhood')?.shortText ??
    ''
  );
};

// Normalize a mixed province/state input to its abbreviation when possible (e.g., "Ontario" → "ON") to keep state consistent.
const normProvinceAbbr = (ctry: string | undefined, raw: string) => {
  if (!raw) return '';
  const dict = ctry === 'CA' ? CA_PROVINCES : ctry === 'US' ? US_PROVINCES : {};
  if (dict[raw]) return dict[raw];
  return Object.values(dict).includes(raw) ? raw : raw;
};

// Lightweight free-text normalizer used during typing/blur: keep only the part before the first comma.
const streetFromFreeText = (v: string) => v.split(',')[0].trim();
// Build "street number + route" using Google components; fall back to the first comma segment if components are missing.
const parseStreetOnly = (
  comps: any[],
  fallbackFormatted: string,
  typed: string
) => {
  const streetNumber = getByType(comps, 'street_number')?.shortText ?? '';
  const route = getByType(comps, 'route')?.shortText ?? '';
  const street = [streetNumber, route].filter(Boolean).join(' ').trim();
  if (street) return street;

  // When structured data fails, fallback to free-text parser
  return streetFromFreeText(fallbackFormatted || typed);
};

// Remove unlicensed provinces from the address question dropdown and will be added back later
const CA_PROVINCES: Record<string, string> = {
  'Newfoundland and Labrador': 'NL',
  'Prince Edward Island': 'PE',
  'Nova Scotia': 'NS',
  'New Brunswick': 'NB',
  //Quebec: 'QC',
  Ontario: 'ON',
  Manitoba: 'MB',
  Saskatchewan: 'SK',
  Alberta: 'AB',
  'British Columbia': 'BC'
  //Yukon: 'YT',
  //'Northwest Territories': 'NT',
  //Nunavut: 'NU'
};

const US_PROVINCES: Record<string, string> = {
  Ohio: 'OH',
  Indiana: 'IN',
  'North Carolina': 'NC',
  Arizona: 'AZ',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Georgia: 'GA',
  Idaho: 'ID',
  Illinois: 'IL',
  Iowa: 'IA',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Michigan: 'MI',
  Minnesota: 'MN',
  Missouri: 'MO',
  Nebraska: 'NE',
  'New Hampshire': 'NH',
  'New Mexico': 'NM',
  'North Dakota': 'ND',
  Oklahoma: 'OK',
  Oregon: 'OR',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Utah: 'UT',
  Virginia: 'VA',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  Arkansas: 'AR',
  Kansas: 'KS',
  Maryland: 'MD',
  'New Jersey': 'NJ',
  Pennsylvania: 'PA',
  Vermont: 'VT',
  Alabama: 'AL',
  Hawaii: 'HI',
  Massachusetts: 'MA',
  Montana: 'MT',
  Nevada: 'NV',
  Washington: 'WA',
  'West Virginia': 'WV',
  Texas: 'TX',
  'New York': 'NY',
  Florida: 'FL',
  California: 'CA',
  Alaska: 'AK',
  Mississippi: 'MS',
  'District Of Columbia': 'DC'
};

const abrevCountry: IObjectKeys = { US: 'US', CA: 'CA' };

const getProvinces = (country?: string) => {
  if (country === abrevCountry.CA) return CA_PROVINCES;
  if (country === abrevCountry.US) return US_PROVINCES;
  return { ...CA_PROVINCES, ...US_PROVINCES };
};

const DEBOUNCE_MS = 250;

interface AddressProps {
  value?: AddressData;
  onChange: (value: AddressData) => void;
  title?: string;
  country?: string;
  province?: string;
  preventStateOrProvinceChange: boolean;
  filterProvince: boolean;
}

const loadFromSessionStorage = (): string => {
  try {
    const serializedState = sessionStorage.getItem('Province_state');
    if (serializedState === null) return '';
    return serializedState;
  } catch {
    return '';
  }
};

const saveToSessionStorage = (state: string): void => {
  try {
    sessionStorage.setItem('Province_state', state);
  } catch {}
};

const getPlacesApiKey = () => (process as any).env?.REACT_APP_PLACES_API_KEY;

// Google Places may return either full province/state names ("Ontario") or abbreviations ("ON").
// Our form and bounding-box logic expect consistent values, so we map names <-> abbreviations in both directions.
// These helpers normalize input/output so the component treats "Ontario" and "ON" as the same province.
const invert = (obj: Record<string, string>) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [v, k]));
const US_ABBR_TO_LONG = invert(US_PROVINCES);
const CA_ABBR_TO_LONG = invert(CA_PROVINCES);
const COUNTRY_LONG = { US: 'USA', CA: 'Canada' } as const;
const toProvinceLong = (ctry?: string, val?: string) => {
  if (!val) return '';
  if (ctry === 'US')
    return US_PROVINCES[val] ? val : US_ABBR_TO_LONG[val] ?? val;
  if (ctry === 'CA')
    return CA_PROVINCES[val] ? val : CA_ABBR_TO_LONG[val] ?? val;
  return val;
};

const toProvinceAbbr = (ctry?: string, longName?: string) => {
  if (!longName) return '';
  if (ctry === 'US') return US_PROVINCES[longName] ?? '';
  if (ctry === 'CA') return CA_PROVINCES[longName] ?? '';
  return '';
};

const Address: React.FC<AddressProps> = (props) => {
  const {
    value,
    onChange,
    title,
    country,
    province: selectedProvinceOrState,
    preventStateOrProvinceChange,
    filterProvince
  } = props;

  // Determine which province/state to filter by: if the incoming prop is a full name (length > 2), store it;
  // otherwise fall back to the last value from sessionStorage (legacy behavior).
  // Convert it to both long/abbr forms so we can filter Google results consistently and still populate our form correctly.
  // Also normalize country code (default CA) because the Google API expects 2-letter codes while UI strings use full country names.
  const propVal = selectedProvinceOrState ?? '';
  const provinceFilteredBy =
    propVal.length > 2
      ? (saveToSessionStorage(propVal), propVal)
      : loadFromSessionStorage();

  const targetProvLong = toProvinceLong(country, provinceFilteredBy);
  const targetProvAbbr = toProvinceAbbr(country, targetProvLong);

  const countryCode = (country ?? 'CA') as 'US' | 'CA';
  const countryLong = COUNTRY_LONG[countryCode];

  const defaultAddress: AddressData = useMemo(() => {
    const base: AddressData = {
      street: value?.street || '',
      province: value?.province || '',
      postalCode: value?.postalCode || '',
      city: value?.city || ''
    };
    if (value?.address2) base.address2 = value.address2;
    return base;
  }, [value]);

  const [address, setAddress] = useState<AddressData>(defaultAddress);
  const [shouldShowOtherFields, setShouldShowOtherFields] = useState(
    !!value?.street
  );
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

  const provices = getProvinces(country);
  const sortedAbrevProvinces = Object.values(provices).sort();
  const postalCodePlaceholder =
    country === abrevCountry.US ? 'Zip Code' : 'Postal Code';

  const updateAddress = (
    updater: Partial<AddressData> | ((prev: AddressData) => AddressData),
    opts?: { replace?: boolean }
  ) => {
    const next: AddressData =
      typeof updater === 'function'
        ? (updater as (prev: AddressData) => AddressData)(address)
        : opts?.replace
        ? (updater as AddressData)
        : { ...address, ...updater };

    setAddress(next);
    onChange(next);
  };

  // Build a Places REST rectangle for the given province/state
  const getProvinceRectangle = (ctry: 'US' | 'CA', provLong: string) => {
    const abbr =
      ctry === 'CA' ? CA_PROVINCES[provLong] : US_PROVINCES[provLong];
    const box =
      ctry === 'CA'
        ? boundingBoxCA[abbr as keyof typeof boundingBoxCA]
        : boundingBoxUS[abbr as keyof typeof boundingBoxUS];
    if (!box) return undefined;

    return {
      rectangle: {
        low: { latitude: box.south, longitude: box.west },
        high: { latitude: box.north, longitude: box.east }
      }
    };
  };

  const [preds, setPreds] = useState<any[]>([]);
  const [openPreds, setOpenPreds] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAutocomplete = async (input: string) => {
    const key = getPlacesApiKey();
    if (!key || !input) {
      setPreds([]);
      setOpenPreds(false);
      setLoading(false);
      return;
    }

    // Province/State bounds → rectangle
    const rect = getProvinceRectangle(countryCode, targetProvLong);
    const body: any = {
      input,
      includedPrimaryTypes: [
        'premise',
        'subpremise',
        'street_address',
        'route'
      ],
      languageCode: 'en',
      includedRegionCodes: [(country ?? 'CA').toUpperCase()],
      includeQueryPredictions: false
    };
    // Only apply a province bias if we have a valid bounding box — same as the old widget,
    // which passed bounds only when available. Without a box, fall back to normal country-level suggestions.
    if (rect) {
      body.locationBias = rect;
    }

    setLoading(true);
    setOpenPreds(true);

    try {
      const r = await fetch(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': key,
            'X-Goog-FieldMask':
              'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat'
          },
          body: JSON.stringify(body)
        }
      );
      const json = await r.json();

      // Adapt to "suggestions" shape
      let list = (json?.suggestions ?? [])
        .map((s: any) => s?.placePrediction)
        .filter(Boolean);
      if (filterProvince) {
        if (!targetProvLong || !targetProvAbbr) {
          list = [];
        } else {
          const stateToken = `, ${targetProvAbbr}`.toLowerCase();
          const countryToken = countryLong.toLowerCase();

          list = list.filter((p: any) => {
            const main =
              p?.structuredFormat?.mainText?.text ?? p?.text?.text ?? '';
            const secondary = p?.structuredFormat?.secondaryText?.text ?? '';
            const label = (secondary
              ? `${main}, ${secondary}`
              : main
            ).toLowerCase();

            // Allow:
            //   "..., Jacksonville, FL, USA"
            //   "..., Jacksonville, FL 32207, USA"
            const hasState = label.includes(stateToken);
            const hasCountry =
              label.includes(`, ${countryToken}`) ||
              label.endsWith(countryToken);

            return hasState && hasCountry;
          });
        }
      }
      setPreds(list);
      setOpenPreds(!!input && list.length > 0);
    } catch (e) {
      setPreds([]);
      setOpenPreds(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceDetails = async (placeId: string) => {
    const key = getPlacesApiKey();
    if (!key || !placeId) return null;

    const fields = encodeURIComponent('addressComponents,formattedAddress');
    const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(
      placeId
    )}?fields=${fields}&key=${encodeURIComponent(key)}`;

    try {
      const r = await fetch(url, { method: 'GET', mode: 'cors' });
      if (!r.ok) {
        // eslint-disable-next-line no-console
        console.error('[Address] fetchPlaceDetails non-200:', r.status);
        return null;
      }
      return await r.json();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Address] fetchPlaceDetails error:', e);
      return null;
    }
  };

  // Emulates old geocodeByAddress for free-text input.
  // Old behavior: Pressing Tab/Enter on a typed address triggered geocodeByAddress().
  // New equivalent: Text Search + Details to resolve free text into structured components
  const resolveFreeTextToDetails = async (raw: string) => {
    const key = getPlacesApiKey();
    const q = (raw || '').trim();
    if (!key || !q) return null;

    const rect = getProvinceRectangle(countryCode, targetProvLong);
    const body: any = {
      textQuery: q,
      languageCode: 'en',
      includedType: 'street_address',
      regionCode: (country ?? 'CA').toUpperCase()
    };
    if (rect) body.locationBias = rect;

    try {
      const r = await fetch(
        'https://places.googleapis.com/v1/places:searchText',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': key,
            'X-Goog-FieldMask': 'places.id'
          },
          body: JSON.stringify(body)
        }
      );
      const json = await r.json();
      const first = json?.places?.[0]?.id;
      if (!first) return null;
      return await fetchPlaceDetails(first);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Address] resolveFreeTextToDetails error:', e);
      return null;
    }
  };
  // Handles a confirmed place prediction (from click or keyboard),
  // fetching details and updating the structured address.
  const pickPrediction = async (p: any) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    try {
      const details = await fetchPlaceDetails(p.placeId);
      const comps = details?.addressComponents ?? [];
      const streetOnly = parseStreetOnly(
        comps,
        details?.formattedAddress ?? '',
        p?.structuredFormat?.mainText?.text ?? p?.text?.text ?? ''
      );
      const provinceRaw =
        getByType(comps, 'administrative_area_level_1')?.shortText ??
        getByType(comps, 'administrative_area_level_1')?.longText ??
        '';
      const province2 = normProvinceAbbr(country, provinceRaw);
      const postalCode2 = getByType(comps, 'postal_code')?.shortText ?? '';
      const city2 = pickCity(comps);
      const subpremise = getByType(comps, 'subpremise')?.shortText ?? '';

      const newAddress: AddressData = {
        street: streetOnly,
        province: province2 || address.province || '',
        postalCode: postalCode2 || address.postalCode || '',
        city: city2 || address.city || ''
      };
      if (subpremise) newAddress.address2 = subpremise;

      setOpenPreds(false);
      setPreds([]);
      setShouldShowOtherFields(true);
      updateAddress(newAddress, { replace: true });
    } catch (e) {
      const raw = p?.structuredFormat?.mainText?.text ?? p?.text?.text ?? '';
      const streetOnly = streetFromFreeText(raw);
      setOpenPreds(false);
      setPreds([]);
      setShouldShowOtherFields(true);
      updateAddress({ street: streetOnly });
    }
  };

  return (
    <div className="input-field-container">
      <div className="mb-2">{title}</div>

      <div className="w-full mb-2 relative widget-container">
        <div className="input-container">
          <input
            placeholder="Street Name"
            className="input-field w-full"
            value={address.street}
            role="combobox"
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded={openPreds}
            onChange={(e) => {
              const val = e.target.value;
              updateAddress({ street: val });
              if (debounceRef.current) clearTimeout(debounceRef.current);
              if (val) {
                debounceRef.current = setTimeout(() => {
                  fetchAutocomplete(val);
                }, DEBOUNCE_MS);
              } else {
                setPreds([]);
                setOpenPreds(false);
              }
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                e.preventDefault();

                // If there are autocomplete predictions, prefer selecting the first one
                // (mirrors old UX: commit the suggested match)
                if (preds.length > 0) {
                  await pickPrediction(preds[0]);
                  return;
                }

                // Otherwise fall back to "free-text geocoding" (old geocodeByAddress behavior)
                const raw = address.street || '';
                const details = await resolveFreeTextToDetails(raw);
                if (details) {
                  const comps = details.addressComponents ?? [];
                  const streetOnly = parseStreetOnly(
                    comps,
                    details.formattedAddress ?? '',
                    raw
                  );
                  const provinceRaw =
                    getByType(comps, 'administrative_area_level_1')
                      ?.shortText ??
                    getByType(comps, 'administrative_area_level_1')?.longText ??
                    '';
                  const province2 = normProvinceAbbr(country, provinceRaw);
                  const postalCode2 =
                    getByType(comps, 'postal_code')?.shortText ?? '';
                  const city2 = pickCity(comps);
                  const subpremise =
                    getByType(comps, 'subpremise')?.shortText ?? '';

                  const newAddress: AddressData = {
                    street: streetOnly,
                    province: province2 || address.province || '',
                    postalCode: postalCode2 || address.postalCode || '',
                    city: city2 || address.city || ''
                  };
                  if (subpremise) newAddress.address2 = subpremise;

                  setOpenPreds(false);
                  setPreds([]);
                  setShouldShowOtherFields(true);
                  updateAddress(newAddress, { replace: true });
                  return;
                }

                // Final fallback: just trim to "street only" (your current behavior)
                const streetOnly = streetFromFreeText(raw);
                if (streetOnly) {
                  updateAddress({ street: streetOnly });
                  setOpenPreds(false);
                  setPreds([]);
                  setShouldShowOtherFields(true);
                }
              }
            }}
            onBlur={() => {
              if (debounceRef.current) clearTimeout(debounceRef.current);
              const streetOnly = streetFromFreeText(address.street || '');
              if (streetOnly && streetOnly !== address.street) {
                updateAddress({ street: streetOnly });
              }
              setTimeout(() => setOpenPreds(false), 120);
            }}
            onFocus={() => {
              if ((address.street || '') && preds.length > 0)
                setOpenPreds(true);
            }}
          />
        </div>

        {(loading || (openPreds && preds.length > 0)) && (
          <div
            className="mb-2 text-base z-[1000] bg-white shadow-lg border overflow-auto"
            role="listbox"
          >
            {loading && (
              <div className="p-3 text-sm opacity-70">Loading...</div>
            )}

            {!loading &&
              preds.map((p: any) => {
                const main =
                  p?.structuredFormat?.mainText?.text ?? p?.text?.text ?? '';
                const secondary =
                  p?.structuredFormat?.secondaryText?.text ?? '';
                const label = secondary ? `${main}, ${secondary}` : main;

                return (
                  <div
                    key={p.placeId}
                    role="option"
                    className="p-4 h-16 border-solid border-2 cursor-pointer border-background hover:bg-background flex items-center whitespace-nowrap overflow-hidden text-ellipsis"
                    title={label}
                    onMouseDown={(e) => {
                      e.preventDefault(); // prevent input blur from swallowing click
                      pickPrediction(p);
                    }}
                  >
                    <span>{label}</span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {shouldShowOtherFields && (
        <>
          <div className="w-full mb-2">
            <input
              placeholder="Unit/Suite #"
              className="input-field w-full"
              value={address.address2 || ''}
              onChange={(e) => {
                updateAddress({ address2: e.target.value });
              }}
            />
          </div>

          <div className="grid grid-rows-2 md:grid-rows-1 grid-cols-2 md:grid-cols-5 gap-2">
            <div className="col-span-2 md:col-span-2">
              <input
                name="city"
                placeholder="City"
                className="input-field w-full"
                value={address.city}
                onChange={(e) => {
                  updateAddress({ city: e.target.value });
                }}
              />
            </div>

            <Select
              placeholder="Province"
              className={`custom-select col-span-1 mt-0 mb-2 md:col-span-1 ${
                preventStateOrProvinceChange ? 'cursor-default' : ''
              }`}
              value={address.province}
              IconComponent={() =>
                preventStateOrProvinceChange ? null : <ExpandMore />
              }
              onChange={({ target }) => {
                if (typeof (target as any).value === 'string') {
                  updateAddress({ province: (target as any).value });
                }
              }}
              disableUnderline
              inputProps={{
                readOnly: preventStateOrProvinceChange,
                className: preventStateOrProvinceChange
                  ? 'cursor-default bg-transparent'
                  : ''
              }}
            >
              {sortedAbrevProvinces.map((p) => (
                <MenuItem
                  style={{
                    paddingTop: 0,
                    borderRadius: 0,
                    backgroundColor: 'white',
                    borderColor: '#403D39'
                  }}
                  key={p}
                  value={p}
                >
                  {p}
                </MenuItem>
              ))}
            </Select>

            <div className="col-span-1 md:col-span-2">
              <input
                placeholder={postalCodePlaceholder}
                className="input-field w-full"
                value={address.postalCode}
                onChange={(e) => {
                  updateAddress({ postalCode: e.target.value });
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Address;
